/**
 * UsageBySourceChart Component
 *
 * Displays a pie/donut chart showing token usage breakdown by source type
 * (workflow, application_assistant, embedding).
 * Uses simple CSS-based visualization (no external chart library required).
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-609 (Frontend: UsageBySourceChart component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React from 'react';
import { Layers, AlertCircle, Workflow, MessageSquare, Hash } from 'lucide-react';
import type { TokenUsageBySource, TokenUsageSourceType } from '../../types/tokenUsage';

export interface UsageBySourceChartProps {
  /** Token usage by source data from API */
  data: TokenUsageBySource[] | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format a number with thousands separator using locale formatting.
 */
const formatNumber = (n: number): string => {
  return n.toLocaleString('en-US');
};

/**
 * Format a number as currency with $ symbol.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 */
const formatCurrency = (n: number): string => {
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

/**
 * Source type display configuration.
 */
interface SourceTypeConfig {
  label: string;
  description: string;
  bgColor: string;
  textColor: string;
  cssColor: string;
  icon: React.FC<{ className?: string }>;
}

const sourceTypeConfig: Record<TokenUsageSourceType, SourceTypeConfig> = {
  orchestration: {
    label: 'Workflow',
    description: 'AI workflow runs',
    bgColor: 'bg-blue-500',
    textColor: 'text-primary-600',
    cssColor: '#3b82f6',
    icon: Workflow,
  },
  application_assistant: {
    label: 'Assistant',
    description: 'Chat conversations',
    bgColor: 'bg-purple-500',
    textColor: 'text-purple-600',
    cssColor: '#a855f7',
    icon: MessageSquare,
  },
  embedding: {
    label: 'Embedding',
    description: 'Document vectorization',
    bgColor: 'bg-green-500',
    textColor: 'text-green-600',
    cssColor: '#22c55e',
    icon: Hash,
  },
};

/**
 * Get configuration for a source type.
 */
const getSourceConfig = (sourceType: TokenUsageSourceType): SourceTypeConfig => {
  return sourceTypeConfig[sourceType] || {
    label: sourceType,
    description: 'Unknown source',
    bgColor: 'bg-gray-500',
    textColor: 'text-gray-600 dark:text-gray-400',
    cssColor: '#6b7280',
    icon: Layers,
  };
};

/**
 * Loading skeleton for the chart.
 */
const LoadingSkeleton: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading source usage data"
    data-testid="source-chart-loading"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4" />
    <div className="flex items-center space-x-8">
      <div className="w-32 h-32 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div className="flex-1 space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-3">
            <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="w-20 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Error state component.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    data-testid="source-chart-error"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
  >
    <div className="flex items-center text-red-600 mb-4">
      <AlertCircle data-testid="source-error-icon" className="w-5 h-5 mr-2" />
      <span className="font-medium">Error loading source data</span>
    </div>
    <p className="text-gray-600 dark:text-gray-400">{message}</p>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC = () => (
  <div
    data-testid="source-chart-empty"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 text-center"
  >
    <Layers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">No source usage data available</p>
    <p className="text-sm text-gray-400 mt-1">Data will appear once AI features are used</p>
  </div>
);

/**
 * Calculate conic gradient for pie chart visualization.
 */
const calculateConicGradient = (data: TokenUsageBySource[]): string => {
  if (data.length === 0) return 'conic-gradient(#e5e7eb 0deg 360deg)';

  const sortedData = [...data].sort((a, b) => b.percentage_of_total - a.percentage_of_total);
  let currentAngle = 0;
  const gradientParts: string[] = [];

  sortedData.forEach((source) => {
    const config = getSourceConfig(source.source_type);
    const angle = (source.percentage_of_total / 100) * 360;
    gradientParts.push(`${config.cssColor} ${currentAngle}deg ${currentAngle + angle}deg`);
    currentAngle += angle;
  });

  return `conic-gradient(${gradientParts.join(', ')})`;
};

/**
 * UsageBySourceChart component displays token usage breakdown by source type.
 */
const UsageBySourceChart: React.FC<UsageBySourceChartProps> = ({
  data,
  loading = false,
  error = null,
}) => {
  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  // Show empty state when no data
  if (!data || data.length === 0) {
    return <EmptyState />;
  }

  // Sort by percentage descending
  const sortedData = [...data].sort((a, b) => b.percentage_of_total - a.percentage_of_total);

  // Calculate totals
  const totalTokens = data.reduce((sum, d) => sum + d.total_tokens, 0);
  const totalCost = data.reduce((sum, d) => sum + d.total_cost, 0);

  return (
    <div
      data-testid="usage-by-source-chart"
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Layers data-testid="source-chart-icon" className="w-5 h-5 text-primary-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Usage by Source</h3>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">{data.length} source{data.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Chart and Legend */}
      <div className="flex items-center space-x-8">
        {/* Pie Chart (CSS-based donut) */}
        <div
          data-testid="source-pie-chart"
          className="relative"
        >
          <div
            className="w-32 h-32 rounded-full"
            style={{
              background: calculateConicGradient(sortedData),
            }}
          >
            {/* Inner circle to create donut effect */}
            <div className="absolute inset-4 bg-white dark:bg-cap-dark rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900 dark:text-warm-white">{data.length}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Sources</div>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div
          data-testid="source-chart-legend"
          className="flex-1 space-y-3"
        >
          {sortedData.map((source, index) => {
            const config = getSourceConfig(source.source_type);
            const IconComponent = config.icon;
            return (
              <div
                key={source.source_type}
                data-testid={`source-legend-${index}`}
                className="flex items-center justify-between group cursor-default"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded ${config.bgColor} flex items-center justify-center`}>
                    <IconComponent className="w-2.5 h-2.5 text-white" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-warm-white">{config.label}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{config.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`font-bold ${config.textColor}`}>
                    {source.percentage_of_total.toFixed(1)}%
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(source.total_cost)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed breakdown */}
      <div
        data-testid="source-chart-details"
        className="mt-6 space-y-3"
      >
        {sortedData.map((source, index) => {
          const config = getSourceConfig(source.source_type);
          const IconComponent = config.icon;
          const barWidth = source.percentage_of_total;

          return (
            <div
              key={source.source_type}
              data-testid={`source-detail-${index}`}
              className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <IconComponent className={`w-4 h-4 ${config.textColor}`} />
                  <span className="font-medium text-gray-900 dark:text-warm-white">{config.label}</span>
                </div>
                <span className={`font-bold ${config.textColor}`}>
                  {source.percentage_of_total.toFixed(1)}%
                </span>
              </div>

              {/* Progress bar */}
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full ${config.bgColor} rounded-full transition-all duration-300`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>

              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>
                  <span className="text-gray-400">Tokens:</span> {formatNumber(source.total_tokens)}
                </span>
                <span>
                  <span className="text-gray-400">Cost:</span> {formatCurrency(source.total_cost)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div
        data-testid="source-chart-summary"
        className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-4 text-sm"
      >
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Tokens:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">{formatNumber(totalTokens)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Cost:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">{formatCurrency(totalCost)}</span>
        </div>
      </div>
    </div>
  );
};

export default UsageBySourceChart;
